import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

import useFilters from '@/hooks/useFilters';
import {businessLimitService} from '@/services/businessLimitService';
import type {BusinessLimit} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';
import {logRejection} from '@/utils/logRejection';

import type {LimitListProps} from './types';

const useLimitList = (): LimitListProps<BusinessLimit> => {
  const [limitList, setLimitList] = useState<BusinessLimit[]>([]);
  const [fullLimitList, setFullLimitList] = useState<BusinessLimit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexQueryParams['status'];
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);

  const {applyFilters} = useFilters(setCurrentFilters);

  // The `status` URL param is layered onto the filters the request is built
  // from rather than mirrored into `currentFilters` state by an effect. That
  // effect ran after the first render and always handed `currentFilters` a
  // fresh object identity, which recreated the fetch callback and fired a
  // second, identical request on every mount. `currentFilters` now holds only
  // the filters the admin picked, so its identity is stable and this memo is
  // stable with it.
  const effectiveFilters = useMemo(
    () => (status ? {...currentFilters, status} : currentFilters),
    [currentFilters, status],
  );

  const fetchAllLimitList = useCallback(
    async (params: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const limitData = await businessLimitService.getBusinessLimitList();
        //set full list and total
        const fullLimitList = limitData.data;
        setFullLimitList(fullLimitList);
        setItemTotal(limitData.total);
        //use page from params or default to 1
        const page = (params.page || 0) + 1;
        setPage(page);
        //calculate start and end indices for the requested page
        const startIndex = (page - 1) * 10;
        const endIndex = startIndex + 10;
        //slice fullLimitList to get 10 items for the current page
        setLimitList(fullLimitList.slice(startIndex, endIndex));
      } catch (e: unknown) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    // The endpoint takes no filter arguments; the dependency is what refetches
    // the list when the admin (or the URL) changes a filter.
    [effectiveFilters],
  );

  // `LimitListProps.fetchList` is `(params) => void` (types.ts) and is handed
  // to Pagination's `onPageChange`, which is also `=> void`, so adapt at the
  // boundary rather than widening either contract. `fetchAllLimitList` catches
  // into `error` state, so this handler only fires if that catch or the finally
  // itself throws. `useCallback` keeps the identity stable.
  const fetchList = useCallback(
    (params: AfriexQueryParams) => {
      fetchAllLimitList(params).catch(
        logRejection('Failed to load limit list'),
      );
    },
    [fetchAllLimitList],
  );

  const handleAutoComplete = (searchTerm: string) => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      setItemTotal(fullLimitList.length);
      setLimitList(fullLimitList.slice(0, 10));
      return;
    }
    const filtered = fullLimitList.filter((item) =>
      item.name?.toLowerCase().includes(trimmed),
    );
    setItemTotal(filtered.length);
    setLimitList(filtered.slice(0, 10));
  };

  useEffect(() => {
    fetchList({});
  }, [fetchList]);

  const handleApplyFilters = useCallback(() => {
    // Layer the URL `status` onto a copy instead of mutating the state object.
    // `applyFilters` receives exactly the same filters as before; the
    // difference is that `filtersToApply` state is no longer written behind
    // React's back, so it stays owned by `selectAppliedFilters`.
    applyFilters(status ? {...filtersToApply, status} : filtersToApply);
    setIsPageReset((prev) => !prev); // Reset pagination to the first page
  }, [applyFilters, filtersToApply, status]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | number | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
    if (status) {
      setCurrentFilters({status});
    }
  };

  return {
    error,
    isLoading,
    pageInfo: {
      total: itemTotal,
      page,
    },
    list: limitList,
    fetchList,
    setPage,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
    handleAutoComplete,
  };
};

export default useLimitList;
